import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const TeamExpertiseSection = () => {
  const teamMembers = [
    {
      name: "Sarah Chen",
      role: "CEO & Co-Founder",
      image: "https://images.unsplash.com/photo-1494790108755-2616b612b786?w=400&h=400&fit=crop&crop=face",
      expertise: ["Enterprise AI Strategy", "Open-Source Leadership", "Vendor Independence"],
      credentials: "Former VP of AI at Microsoft, Led 200+ AI transformations, Contributor to TensorFlow",
      quote: "AI independence isn't just about technology—it's about organizational freedom."
    },
    {
      name: "Dr. Marcus Rodriguez",
      role: "CTO & Co-Founder",
      image: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=400&h=400&fit=crop&crop=face",
      expertise: ["Open-Source AI Development", "MLOps Architecture", "Technical Leadership"],
      credentials: "PhD in Machine Learning, Core contributor to PyTorch, 15+ years in enterprise AI",
      quote: "Open-source AI gives you the keys to your own kingdom."
    },
    {
      name: "Jennifer Park",
      role: "VP of Strategy",
      image: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=400&h=400&fit=crop&crop=face",
      expertise: ["Business Transformation", "ROI Optimization", "Change Management"],
      credentials: "Former McKinsey Partner, MBA from Wharton, Specialized in AI business cases",
      quote: "The best AI strategy is the one you control completely."
    },
    {
      name: "David Kim",
      role: "Head of Engineering",
      image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=400&fit=crop&crop=face",
      expertise: ["Kubernetes & MLOps", "Cloud Architecture", "System Integration"],
      credentials: "Former Google Cloud Architect, Certified Kubernetes Expert, Open-source maintainer",
      quote: "Infrastructure independence is the foundation of AI independence."
    }
  ];

  const achievements = [
    {
      icon: "Award",
      title: "Industry Recognition",
      items: [
        "AI Excellence Award 2023",
        "Open Source Champion 2022",
        "Enterprise Innovation Leader 2021"
      ]
    },
    {
      icon: "Code2",
      title: "Open-Source Contributions",
      items: [
        "12M+ lines of code contributed",
        "Core maintainers of 15+ projects",
        "500+ community contributions"
      ]
    },
    {
      icon: "Users",
      title: "Enterprise Impact",
      items: [
        "500+ successful transformations",
        "$2.5B in cost savings delivered",
        "95% client satisfaction rate"
      ]
    }
  ];

  return (
    <section className="py-20 lg:py-32 bg-brand-surface">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 bg-brand-primary/10 rounded-full px-4 py-2 mb-6">
            <Icon name="Users" size={16} className="text-brand-primary" />
            <span className="text-sm font-medium text-brand-primary">Expert Team</span>
          </div>
          
          <h2 className="text-3xl lg:text-4xl font-bold text-text-primary mb-6">
            Deep Expertise in Both
            <span className="block text-brand-primary">Enterprise & Open-Source</span>
          </h2>
          
          <p className="text-lg text-text-secondary max-w-3xl mx-auto">
            Our team combines decades of enterprise AI experience with deep open-source community involvement, giving us unique insights into both worlds.
          </p>
        </div>

        {/* Team Members */}
        <div className="grid md:grid-cols-2 gap-8 mb-20">
          {teamMembers?.map((member, index) => (
            <div key={index} className="bg-white rounded-xl shadow-executive p-8 hover:shadow-executive-lg transition-all duration-300">
              <div className="flex items-start space-x-6">
                <div className="flex-shrink-0">
                  <Image
                    src={member?.image}
                    alt={member?.name}
                    className="w-20 h-20 rounded-full object-cover"
                  />
                </div>
                
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-text-primary mb-1">
                    {member?.name}
                  </h3>
                  <p className="text-brand-primary font-medium mb-3">
                    {member?.role}
                  </p>
                  
                  <div className="flex flex-wrap gap-2 mb-4">
                    {member?.expertise?.map((skill, skillIndex) => (
                      <span
                        key={skillIndex}
                        className="px-3 py-1 bg-brand-primary/10 text-brand-primary text-sm rounded-full"
                      >
                        {skill}
                      </span>
                    ))}
                  </div>
                  
                  <p className="text-sm text-text-secondary mb-4 leading-relaxed">
                    {member?.credentials}
                  </p>
                  
                  <blockquote className="text-sm italic text-text-primary border-l-3 border-brand-accent pl-4">
                    "{member?.quote}"
                  </blockquote>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Achievements */}
        <div className="grid md:grid-cols-3 gap-8">
          {achievements?.map((achievement, index) => (
            <div key={index} className="bg-white rounded-xl shadow-executive p-8 text-center">
              <div className="w-16 h-16 bg-brand-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <Icon name={achievement?.icon} size={32} className="text-brand-primary" />
              </div>
              
              <h3 className="text-xl font-bold text-text-primary mb-6">
                {achievement?.title}
              </h3>
              
              <ul className="space-y-3">
                {achievement?.items?.map((item, itemIndex) => (
                  <li key={itemIndex} className="flex items-center justify-center space-x-2">
                    <Icon name="CheckCircle" size={16} className="text-brand-accent flex-shrink-0" />
                    <span className="text-text-secondary">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default TeamExpertiseSection;